import React, { type FC } from 'react';
import { FileText } from 'lucide-react';
import { CardShell } from './card-shell';
import type { ToolCardProps } from './types';
import { safeGet } from './types';

/** Format diff values: handles strings, arrays of objects (highlights, skillGroups), etc. */
function formatDiffValue(val: unknown): string {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (Array.isArray(val)) {
    return val
      .map((item: any) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          // Highlights: { title, desc }
          if (item.title && item.desc) return `${item.title}: ${item.desc}`;
          if (item.title) return item.title;
          // Skill groups: { category, items }
          if (item.category && Array.isArray(item.items)) return `${item.category}: ${item.items.join(', ')}`;
          // Generic: first string value
          const firstStr = Object.values(item).find((v): v is string => typeof v === 'string');
          if (firstStr) return firstStr;
        }
        return JSON.stringify(item);
      })
      .join(' | ');
  }
  return JSON.stringify(val);
}

const SECTION_LABELS: Record<string, string> = {
  title: 'Professional Title',
  summary: 'Professional Summary',
  highlights: 'Career Highlights',
  skillGroups: 'Skill Groups',
};

export const CvSectionPreviewCard: FC<ToolCardProps> = ({ result }) => {
  if (!result || typeof result !== 'object') return null;

  const section = safeGet<string>(result, 'section') ?? '';
  const mode = safeGet<string>(result, 'mode') ?? '';
  const diff = safeGet<Record<string, unknown>>(result, 'diff');
  const health = safeGet<Record<string, unknown>>(result, 'health');
  const display = safeGet<Record<string, unknown>>(result, 'display');

  const changed = safeGet<boolean>(diff, 'changed');
  const beforeRaw = safeGet<unknown>(diff, 'before');
  const afterRaw = safeGet<unknown>(diff, 'after');
  const before = formatDiffValue(beforeRaw);
  const after = formatDiffValue(afterRaw);
  const diffSummary = safeGet<string>(diff, 'summary') ?? '';

  const grounded = safeGet<boolean>(health, 'grounded');
  const sectionLabel = SECTION_LABELS[section] || section;
  const displayTitle = safeGet<string>(display, 'title') || `${sectionLabel} Preview`;

  return (
    <CardShell>
      <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileText size={15} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
            {displayTitle}
          </span>
        </div>

        {/* Section + Mode + Health badges */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <span className="tabbio-menu-badge">{sectionLabel}</span>
          {mode && (
            <span className="tabbio-menu-badge">{mode}</span>
          )}
          {grounded && (
            <span className="tabbio-menu-badge">Grounded</span>
          )}
        </div>

        {/* Diff: Before → After */}
        {(before || after) && (
          <div style={{
            borderRadius: 10, border: '1px solid var(--gem-outline-variant)',
            overflow: 'hidden',
          }}>
            {before && (
              <div style={{
                padding: '10px 12px', fontSize: 13, lineHeight: '18px',
                background: 'rgba(0,0,0,0.04)', color: 'var(--text-tertiary)',
                borderBottom: after ? '1px solid var(--gem-outline-variant)' : undefined,
              }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginRight: 8 }}>Before</span>
                {before.slice(0, 300)}
              </div>
            )}
            {after && (
              <div style={{
                padding: '10px 12px', fontSize: 13, lineHeight: '18px',
                color: 'var(--text-primary)',
              }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginRight: 8 }}>After</span>
                <span style={{ fontWeight: 500 }}>
                  {after.slice(0, 300)}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Diff summary */}
        {diffSummary && (
          <div style={{ fontSize: 13, color: 'var(--text-tertiary)', lineHeight: '18px' }}>
            {changed ? 'Changed' : 'No change'}: {diffSummary}
          </div>
        )}
      </div>
    </CardShell>
  );
};
